import { useEffect, useRef, useState } from 'react';

import type { AxiosError } from 'axios';

export type BulkAssignmentOption = { value: number; label: string };
type Failure = { id: number; label: string; message: string };

export default function useBulkAssignments() {
  const [selected, setSelected] = useState<BulkAssignmentOption[]>([]);
  const [completedIds, setCompletedIds] = useState<Set<number>>(new Set());
  const [failures, setFailures] = useState<Failure[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [progress, setProgress] = useState<{ added: number; failed: number } | null>(null);
  const running = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const submit = async (
    assign: (option: BulkAssignmentOption) => Promise<unknown>,
    onAssignmentsChanged?: () => void
  ): Promise<boolean> => {
    if (!mounted.current || running.current || !selected.length) return false;
    running.current = true;
    setIsSaving(true);
    setFailures([]);
    setProgress(null);
    const pending = Array.from(new Map(selected.map((option) => [option.value, option])).values())
      .filter((option) => !completedIds.has(option.value));
    const failed: BulkAssignmentOption[] = [];
    const errors: Failure[] = [];
    let added = 0;

    try {
      for (const option of pending) {
        if (!mounted.current) return false;
        try {
          await assign(option);
          if (!mounted.current) return false;
          added++;
          setCompletedIds((previous) => new Set(previous).add(option.value));
        } catch (error) {
          if (!mounted.current) return false;
          failed.push(option);
          errors.push({
            id: option.value,
            label: option.label,
            message: (error as AxiosError<{ message?: string }>)?.response?.data?.message || 'Unable to add. Please try again.'
          });
        }
        setProgress({ added, failed: failed.length });
      }
      setSelected(failed);
      setFailures(errors);
      if (added > 0 && failed.length > 0) onAssignmentsChanged?.();
      return pending.length > 0 && failed.length === 0;
    } finally {
      running.current = false;
      if (mounted.current) setIsSaving(false);
    }
  };

  return { selected, setSelected, completedIds, failures, isSaving, progress, submit };
}
